// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — Followups & Appointments data hooks (Phase 0.1)
//
// One hook family serves both entities: they share a shape and differ only in
// their date field, so the endpoint segment is a parameter. Mutations
// invalidate the parent's list plus the overdue/due-today feeds, because
// logging a followup is exactly what removes a row from the overdue widget.
// ─────────────────────────────────────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export type TimelineKind = 'followups' | 'appointments';

/** Every record type a followup/appointment can hang off (mirrors PARENT_TYPES). */
export type ParentType =
  | 'lead' | 'deal' | 'enquiry' | 'quotation' | 'proforma'
  | 'invoice' | 'sales' | 'sample' | 'service_ticket' | 'telecall' | 'visit';

export type FollowupStatus =
  | 'open' | 'done' | 'postponed' | 'not_interested' | 'interested' | 'closed';

export type AppointmentStatus =
  | 'open' | 'appointment' | 'not_interested' | 'interested' | 'pending';

export interface Followup {
  _id: string;
  parentType: ParentType;
  parentId: string;
  followupNote: string;
  details: string;
  followupDate: string;
  status: FollowupStatus;
  representativeId: string;
  closingDate: string | null;
  contactPhone: string;
  contactEmail: string;
  /** Who was spoken to — required by "Complete this lead" (2026-10-28). */
  contactPersonName?: string;
  contactPersonPhone?: string;
  contactPersonDesignation?: string;
  contactPersonEmail?: string;
  notes: string;
  created_at: string;
  contactMethod: string;
  customerResponse: string;
  requirement: string;
  nextAction: string;
  followupCategory: string;
  customCategory?: string;
  /** Set when closed — see CloseFollowupDialog (client, 2026-10-28). */
  outcome?: string;
  /** Closed with "No next follow-up needed" — why (2026-10-30). */
  noNextReason?: string;
}

export const CONTACT_METHOD_OPTIONS = [
  { value: '', label: 'Not recorded' },
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'visit', label: 'Visit' },
  { value: 'other', label: 'Other' },
] as const;

export const FOLLOWUP_CATEGORY_OPTIONS = [
  { value: 'order', label: 'Order Follow-up' },
  { value: 'payment', label: 'Payment Follow-up' },
  { value: 'visit', label: 'Visit Follow-up' },
  { value: 'feedback', label: 'Trial Feedback' },
  { value: 'custom', label: 'Custom' },
  { value: 'other', label: 'Other' },
] as const;

export interface Appointment {
  _id: string;
  parentType: ParentType;
  parentId: string;
  appointmentDate: string;
  status: AppointmentStatus;
  representativeId: string;
  details: string;
  minutesOfMeeting: string;
  reason: string;
  notes: string;
  created_at: string;
}

export type TimelineRow = Followup | Appointment;

/** The date field differs between the two entities. */
export const dateFieldFor = (kind: TimelineKind): 'followupDate' | 'appointmentDate' =>
  kind === 'followups' ? 'followupDate' : 'appointmentDate';

export const dateOf = (kind: TimelineKind, row: TimelineRow): string =>
  kind === 'followups'
    ? (row as Followup).followupDate
    : (row as Appointment).appointmentDate;

const listKey = (kind: TimelineKind, parentType: ParentType | 'any', parentId: string) =>
  [kind, 'list', parentType, parentId] as const;

/** Followups/appointments logged against one record, newest first. */
export function useTimelineList(
  kind: TimelineKind,
  parentType: ParentType,
  // Detail drawers hold their id as `string | null` while closed, so accept it
  // directly rather than making every caller coerce.
  parentId: string | null | undefined,
  // A pipeline record is one document whose followups were logged as 'lead'
  // or 'deal' depending on its stage at the time — list them all by id.
  opts: { anyParentType?: boolean } = {},
) {
  return useQuery({
    queryKey: listKey(kind, opts.anyParentType ? 'any' : parentType, parentId ?? ''),
    enabled: Boolean(parentId),
    queryFn: async () => {
      const params = new URLSearchParams(opts.anyParentType
        ? { parentId: parentId as string }
        : { parentType, parentId: parentId as string });
      const { data } = await api.get<Record<string, TimelineRow[]>>(
        `/api/${kind}?${params.toString()}`,
      );
      return data[kind] ?? [];
    },
  });
}

/**
 * Outstanding rows across records — the dashboard widgets.
 * `scope` is a request, not a grant: the backend clamps it to what the caller's
 * role already allows and echoes back what it actually applied.
 */
export function useOutstanding(
  kind: TimelineKind,
  which: 'overdue' | 'due-today' | 'upcoming',
  scope: 'mine' | 'team' | 'all' = 'mine',
  parentType?: ParentType,
) {
  return useQuery({
    queryKey: [kind, which, scope, parentType ?? 'any'],
    queryFn: async () => {
      const params = new URLSearchParams({ scope });
      if (parentType) params.set('parentType', parentType);
      const { data } = await api.get<Record<string, unknown>>(
        `/api/${kind}/${which}?${params.toString()}`,
      );
      return {
        rows: (data[kind] as TimelineRow[]) ?? [],
        count: (data.count as number) ?? 0,
        scope: (data.scope as string) ?? scope,
      };
    },
  });
}

function useInvalidate(kind: TimelineKind) {
  const qc = useQueryClient();
  return (parentType: ParentType, parentId: string) => {
    qc.invalidateQueries({ queryKey: listKey(kind, parentType, parentId) });
    // Logging or settling a row is precisely what changes these feeds.
    qc.invalidateQueries({ queryKey: [kind] });
    qc.invalidateQueries({ queryKey: [`${kind}-overdue`] });
    qc.invalidateQueries({ queryKey: [`${kind}-due-today`] });
    qc.invalidateQueries({ queryKey: [`${kind}-upcoming`] });
    qc.invalidateQueries({ queryKey: ['dashboard-kpi'] });
  };
}

export function useCreateTimelineRow(kind: TimelineKind) {
  const invalidate = useInvalidate(kind);
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api.post(`/api/${kind}`, payload);
      return data;
    },
    onSuccess: (_data, payload) => {
      invalidate(payload.parentType as ParentType, String(payload.parentId));
    },
  });
}

export function useUpdateTimelineRow(kind: TimelineKind) {
  const invalidate = useInvalidate(kind);
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const { data } = await api.patch(`/api/${kind}/${id}`, patch);
      return data;
    },
    onSuccess: (_data, vars) => {
      const p = vars.patch as { parentType?: ParentType; parentId?: string };
      if (p.parentType && p.parentId) invalidate(p.parentType, p.parentId);
    },
  });
}
